// Diamètres proposés uniquement : aucun choix ni coefficient de charge prérempli.
export const DIAMETRES_FRIGORIFIQUES = [
  ['1/4', '6.35'], ['3/8', '9.52'], ['1/2', '12.7'], ['5/8', '15.88'], ['3/4', '19.05'],
  ['7/8', '22.22'], ['1 1/8', '28.58'], ['1 3/8', '34.92'], ['1 5/8', '41.28'],
] as const;
// Mention générale : les contrôles (plafonds de la notice, concentration, F-Gas) sont SIGNALÉS, jamais déclarés conformes.
export const AVERTISSEMENT_CHARGE = 'TRAME signale des indicateurs (plafonds de la notice, concentration dans la plus petite pièce, F-Gas) sans jamais déclarer une installation conforme : à vérifier par le référent.';
/** Tube en pouces (désignation « 1/4 »…) par défaut ; un tube métrique n'a pas de désignation en pouces. */
export interface DiametreFrigorifique { pouces: string; mm: string; norme?: 'pouce' | 'metrique' }
export interface Refrigeration {
  liquide: DiametreFrigorifique | null; gaz: DiametreFrigorifique | null;
  isolantMm: string | null; fluide: string | null; fabricant: string | null; gamme: string | null;
  uniteExterieure: string | null; unitesInterieures: string[];
  derivations: { point: number; reference: string | null }[];
}
export interface VerticalSpec {
  mode: 'manuel' | 'etages' | 'a_saisir'; metres: string | null; source: string | null; destinationPlanId: string | null;
}
export interface RegleCharge {
  id: string; manufacturer: string; range_name: string; fluid: string; liquid_mm: string; grams_per_m: string;
  fixed_grams_per_unit: string | null; source_document: string; source_page: number; source_date: string;
  validated_by: string | null; validated_at: string | null; updated_at: string;
  // Colonnes ajoutées le 22/10/2026 (null pour une ancienne règle, « ancien format »).
  ruleset_id?: string | null; pipe_standard?: 'pouce' | 'metrique' | null; liquid_inch?: string | null; liquid_mm_source?: string | null;
  length_condition_min_m?: string | null; length_condition_max_m?: string | null; source_value?: string | null; source_unit?: string | null;
}

// ─── Notices de fabricant (jeux de règles) — 22/10/2026 ───
export const TYPES_SYSTEME = [['split', 'Split (une UE, une UI)'], ['multisplit', 'Multisplit'], ['drv_2_tubes', 'DRV / VRF 2 tubes'], ['drv_3_tubes', 'DRV / VRF 3 tubes (récupération)']] as const;
export const BASES_LONGUEUR = [
  ['total_liquide', 'Toute la longueur de liquide (aucune déduction)'],
  ['au_dela_precharge_total', 'Au-delà de la longueur préchargée, sur la longueur totale'],
  ['au_dela_precharge_par_liaison', 'Au-delà de la longueur préchargée, liaison par liaison'],
] as const;
export const MODES_ARRONDI = [['superieur', 'Par excès (au pas supérieur)'], ['plus_proche', 'Au plus proche']] as const;
export const UNITES_COEFFICIENT = ['kg/m', 'g/m', 'lb/ft', 'oz/ft'] as const;
export const UNITES_MASSE = ['g', 'kg', 'lb', 'oz'] as const;
export const TYPES_FORFAIT = [
  ['par_ue', 'Forfait par modèle d’unité extérieure (ou module)'],
  ['charge_usine_ue', 'Charge d’usine du modèle d’UE (sert à la charge totale)'],
  ['max_ajout_ue', 'Charge ajoutée maximale du modèle d’UE'],
  ['par_boitier', 'Forfait par boîtier de dérivation (BS / BC / HRU)'],
  ['par_ui', 'Forfait par modèle d’unité intérieure'],
  ['taux_raccordement', 'Forfait par UE si le taux de raccordement dépasse un seuil'],
  ['tranche_capacite_ui', 'Forfait par tranche de capacité totale des UI'],
] as const;
export const CLASSES_SECURITE = ['A1', 'A2L', 'A2', 'A3', 'B1', 'B2L', 'B2', 'B3'] as const;
export type TypeForfait = typeof TYPES_FORFAIT[number][0];

export interface NoticeCharge {
  id: string; manufacturer: string; range_name: string; fluid: string; outdoor_models: string[] | null;
  system_kind: string | null; length_basis: string | null; precharged_length_m: string | null; precharge_applies_to_liquid_inch: string | null;
  length_multiplier: string | null; rounding_step_g: string | null; rounding_mode: 'superieur' | 'plus_proche' | null;
  max_added_charge_g: string | null; max_total_charge_g: string | null; warn_total_charge_g: string | null;
  max_longest_m: string | null; max_after_first_branch_m: string | null; max_total_length_m: string | null;
  max_height_ou_iu_m: string | null; max_height_iu_iu_m: string | null; source_unit: string | null;
  source_document: string; source_page: number; source_date: string; validated_by: string | null; validated_at: string | null; updated_at: string;
}
export interface ForfaitCharge {
  id: string; ruleset_id: string; kind: TypeForfait; model_pattern: string | null; threshold_min: string | null; threshold_max: string | null;
  grams: string; source_value: string; source_unit: string; source_page: number; updated_at: string;
}
export interface FluideReference {
  id: string; name: string; safety_class: string | null; practical_limit_kg_m3: string | null; lfl_kg_m3: string | null;
  gwp: string | null; gwp_source: string | null; regulatory_substance: string | null; standard_edition: string | null;
  source_document: string; source_page: number; source_date: string; validated_by: string | null; validated_at: string | null; updated_at: string;
}
export interface PrgReglementaire { substance: string; aliases: string[]; gwp: string; source: string }

/** Données d'équipement d'un système (fiche), toutes sourcées ; null = à saisir. */
export interface DonneesSysteme {
  modules: { modele: string }[];
  unites: { repere: string; modele: string | null; indice: string | null; zone: string | null; surfaceM2: string | null; hauteurM: string | null;
    h0M: string | null; surfaceMinNoticeM2: string | null; sourcePiece: string | null }[];
  boitiers: { repere: string; modele: string | null }[];
  longueurPlusEloigneeM: string | null; longueurPlusEloigneeSource: string | null;
  tauxRaccordementPct: string | null; tauxSource: string | null;
  apresPremiereDerivationM: string | null; deniveleUeUiM: string | null; deniveleUiUiM: string | null; mesuresSource: string | null;
  detectionFuite: boolean | null;
}
export const donneesSystemeVides = (): DonneesSysteme => ({ modules: [], unites: [], boitiers: [], longueurPlusEloigneeM: null, longueurPlusEloigneeSource: null,
  tauxRaccordementPct: null, tauxSource: null, apresPremiereDerivationM: null, deniveleUeUiM: null, deniveleUiUiM: null, mesuresSource: null, detectionFuite: null });

/** Contrôle d'étanchéité périodique : seuils LÉGAUX (universels), règlement (UE) 2024/573. Aucune valeur d'un client. */
export const FGAZ_REGLEMENT = {
  source: 'Règlement (UE) 2024/573, art. 5 § 1 et § 6 (périodicité), art. 6 § 1 (détection obligatoire)',
  seuilControleT: 5,
  paliers: [
    { minT: 5, maxT: 50, sansDetectionMois: 12, avecDetectionMois: 24 },
    { minT: 50, maxT: 500, sansDetectionMois: 6, avecDetectionMois: 12 },
    { minT: 500, maxT: null as number | null, sansDetectionMois: 3, avecDetectionMois: 6 },
  ],
  seuilDetectionObligatoireT: 500,
} as const;
export const RAPPEL_CERFA = 'Fiche d’intervention CERFA 15497*04 à établir à la mise en service (quantité chargée), conservée 5 ans par le détenteur et par l’opérateur (art. R. 543-82 du code de l’environnement) ; registre de l’équipement à ouvrir (art. 7 du règlement (UE) 2024/573).';
